import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Center,
    Group,
    Loader,
    Modal,
    Select,
    Stack,
    Switch,
    Tabs,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertTriangle, Clock, EyeOff, Info, Lock, MapPin, Server, ShieldCheck } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import SettingRow from '@/design/SettingRow';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { GovernanceSettingsViewModel } from './GovernanceSettingsViewModel';
import type { DataExclusionKey, RetentionCategory, RetentionValue } from './GovernanceService';

const DATA_REGION = 'us-east-2';
const DATA_REGION_LABEL = 'US East (Ohio)';

const RETENTION_OPTIONS = [
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
    { value: '180', label: '180 days' },
    { value: '365', label: '365 days' },
    { value: 'forever', label: 'Keep indefinitely' },
];

const INHERIT = '__inherit__';

interface DataTypeMeta {
    key: RetentionCategory;
    label: string;
    description: string;
}

const DATA_TYPES: DataTypeMeta[] = [
    { key: 'telemetry', label: 'Telemetry & usage events', description: 'Raw usage events and intermediate processing from connected providers.' },
    { key: 'audit', label: 'Audit logs', description: 'Admin actions, permission and access changes.' },
    { key: 'insights', label: 'Analytics history', description: 'Computed scorecards, daily rollups, and cost forecasts.' },
    { key: 'licenses', label: 'License history', description: 'License assignment history and provider sync records.' },
];

interface ExclusionMeta {
    key: DataExclusionKey;
    label: string;
    description: string;
}

const EXCLUSIONS: ExclusionMeta[] = [
    {
        key: 'promptResponseContent',
        label: 'Prompt & response content',
        description: 'Drop the text of prompts and model responses at ingestion — this also removes any PII, IP addresses, or file snippets embedded in that text.',
    },
    { key: 'fileContents', label: 'File contents', description: 'Never retain the contents of files referenced in usage.' },
    { key: 'rawIpAddresses', label: 'Raw IP addresses', description: 'Scrub source IP addresses from retained content.' },
    { key: 'endUserPii', label: 'End-user PII', description: 'Scrub personal identifiers (e.g. email addresses) from retained content.' },
];

interface SubprocessorMeta {
    name: string;
    purpose: string;
}

const SUBPROCESSORS: SubprocessorMeta[] = [
    { name: 'Amazon Web Services', purpose: `Cloud hosting, object storage, and email delivery (${DATA_REGION}).` },
    { name: 'Anthropic (via Amazon Bedrock)', purpose: 'AI usage classification and summarization.' },
    { name: 'Your connected AI providers', purpose: 'OpenAI, Google, Microsoft, and others you connect — the source of the usage data you ingest.' },
];

function toSelectValue(value: RetentionValue): string {
    return value === null ? 'forever' : String(value);
}

function fromSelectValue(raw: string): RetentionValue {
    return raw === 'forever' ? null : Number.parseInt(raw, 10);
}

/** A titled section inside a tab card. */
function Section({ title, description, children }: { title: string; description: string; children: ReactNode }): ReactElement {
    return (
        <Box>
            <Text size="sm" fw={600}>
                {title}
            </Text>
            <Text size="xs" c="dimmed" mb="md">
                {description}
            </Text>
            {children}
        </Box>
    );
}

function DataResidencyCard(): ReactElement {
    return (
        <SurfaceCard $padding="lg" $static style={{ cursor: 'default', marginBottom: 'var(--mantine-spacing-xl)' }}>
            <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Group gap="md" wrap="nowrap">
                    <Box
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 'var(--bm-radius)',
                            background: 'var(--bm-color-success-soft)',
                            border: '1px solid var(--bm-color-success-border)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <MapPin size={20} color="var(--bm-color-success)" />
                    </Box>
                    <Box>
                        <Group gap="xs">
                            <Text size="sm" fw={600}>
                                Data residency
                            </Text>
                            <Badge size="xs" variant="light" color="gray" leftSection={<Lock size={10} />}>
                                Fixed
                            </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" mt={4}>
                            Your data is stored and processed in{' '}
                            <Text span fw={600} style={{ color: 'var(--bm-color-text)' }}>
                                {DATA_REGION}
                            </Text>{' '}
                            ({DATA_REGION_LABEL}). The region is set at the infrastructure level for your deployment and
                            cannot be changed from here.
                        </Text>
                    </Box>
                </Group>
                <Lock size={16} color="var(--bm-color-text-dimmed)" style={{ flexShrink: 0 }} />
            </Group>
        </SurfaceCard>
    );
}

function RetentionTab({ vm }: { vm: GovernanceSettingsViewModel }): ReactElement | null {
    if (!vm.policy) return null;
    const { policy } = vm;
    return (
        <SurfaceCard $padding="lg" $static style={{ cursor: 'default' }}>
            <Stack gap="xl">
                <Section
                    title="Default retention period"
                    description="How long usage and telemetry are kept before being automatically purged."
                >
                    <Select
                        data={RETENTION_OPTIONS}
                        value={toSelectValue(policy.defaultRetentionDays)}
                        onChange={(v) => v && vm.setDefaultRetention(fromSelectValue(v))}
                        maw={260}
                        allowDeselect={false}
                    />
                </Section>

                <Section title="Retention by data type" description="Override the default for specific categories of data.">
                    {DATA_TYPES.map((d, i) => {
                        const override = policy.retentionOverrides[d.key];
                        const value = override === undefined ? INHERIT : toSelectValue(override);
                        return (
                            <SettingRow
                                key={d.key}
                                title={d.label}
                                description={d.description}
                                last={i === DATA_TYPES.length - 1}
                                control={
                                    <Select
                                        data={[{ value: INHERIT, label: 'Use default' }, ...RETENTION_OPTIONS]}
                                        value={value}
                                        onChange={(v) => {
                                            if (v === null || v === INHERIT) vm.clearOverride(d.key);
                                            else vm.setOverride(d.key, fromSelectValue(v));
                                        }}
                                        w={200}
                                        allowDeselect={false}
                                    />
                                }
                            />
                        );
                    })}
                </Section>
            </Stack>
        </SurfaceCard>
    );
}

function ExclusionsTab({ vm }: { vm: GovernanceSettingsViewModel }): ReactElement | null {
    if (!vm.policy) return null;
    const { policy } = vm;
    return (
        <SurfaceCard $padding="lg" $static style={{ cursor: 'default' }}>
            <Stack gap="md">
                <Section
                    title="Data we should never keep"
                    description="When enabled, this data is dropped or scrubbed during processing and never written to the retained dataset."
                >
                    {EXCLUSIONS.map((e, i) => (
                        <SettingRow
                            key={e.key}
                            title={e.label}
                            description={e.description}
                            last={i === EXCLUSIONS.length - 1}
                            control={
                                <Switch checked={policy.exclusions[e.key]} onChange={() => vm.toggleExclusion(e.key)} />
                            }
                        />
                    ))}
                </Section>
                {policy.exclusions.promptResponseContent && (
                    <Alert variant="light" color="yellow" icon={<AlertTriangle size={16} />} title="Limited functionality">
                        With prompt &amp; response content excluded, usage classification and insights fall back to
                        token-only estimates. Task, topic, and difficulty analytics will be unavailable for newly
                        ingested data.
                    </Alert>
                )}
            </Stack>
        </SurfaceCard>
    );
}

function SubprocessorsTab(): ReactElement {
    return (
        <SurfaceCard $padding="lg" $static style={{ cursor: 'default' }}>
            <Section
                title="Subprocessors"
                description="Third parties that process your data to run Ultra Trace. These are infrastructure the product runs on, not optional integrations."
            >
                {SUBPROCESSORS.map((s, i) => (
                    <SettingRow
                        key={s.name}
                        title={s.name}
                        description={s.purpose}
                        last={i === SUBPROCESSORS.length - 1}
                        leading={
                            <Box
                                style={{
                                    width: 30,
                                    height: 30,
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'var(--mantine-color-gray-light)',
                                    borderRadius: 'var(--bm-radius)',
                                }}
                            >
                                <Server size={15} />
                            </Box>
                        }
                        control={
                            <Badge variant="light" color="gray" size="sm">
                                Required
                            </Badge>
                        }
                    />
                ))}
            </Section>
            <Alert variant="light" color="gray" icon={<Info size={16} />} mt="md">
                Data leaves your region only as needed for these subprocessors to operate, under the platform&apos;s data
                processing terms.
            </Alert>
        </SurfaceCard>
    );
}

function DangerTab({ onDelete }: { onDelete: () => void }): ReactElement {
    return (
        <SurfaceCard
            $padding="lg"
            $static
            style={{
                cursor: 'default',
                borderColor: 'var(--bm-color-danger-border)',
                background: 'var(--bm-color-danger-soft)',
            }}
        >
            <Group gap="xs" mb="xs">
                <AlertTriangle size={16} color="var(--bm-color-danger)" />
                <Text size="sm" fw={700} style={{ color: 'var(--bm-color-danger-text)' }}>
                    Delete all data
                </Text>
            </Group>
            <Text size="xs" c="dimmed" mb="md">
                Permanently delete all collected data — telemetry, analytics, license history, and insights. Your
                account, settings, connections, and taxonomy stay intact, and new data will accrue again. This cannot be
                undone.
            </Text>
            <Button color="red" onClick={onDelete}>
                Delete all data
            </Button>
        </SurfaceCard>
    );
}

/**
 * Settings → Data & Retention. Retention windows and ingestion exclusions are
 * persisted to the tenant's governance policy (bmai-billing) and enforced by each
 * data-owning service; the danger zone triggers a data-only purge (collected data
 * removed, account kept). Subprocessors are a read-only transparency disclosure.
 */
export default function DataRetentionSettingsPage(): ReactElement {
    const vm = useViewModel(GovernanceSettingsViewModel);
    useEffect(() => vm.init(), [vm]);

    const [tab, setTab] = useState<string | null>('retention');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    const closeDelete = (): void => {
        setDeleteOpen(false);
        setConfirmText('');
    };

    const confirmDelete = async (): Promise<void> => {
        const ok = await vm.requestPurge();
        closeDelete();
        notifications.show(
            ok
                ? { title: 'Deletion started', message: 'Your collected data has been queued for permanent deletion.', color: 'red' }
                : { title: 'Could not start deletion', message: vm.saveError ?? 'Please try again.', color: 'red' },
        );
    };

    const save = async (): Promise<void> => {
        const ok = await vm.save();
        notifications.show(
            ok
                ? { title: 'Settings saved', message: 'Your data & retention policy has been updated.', color: 'green' }
                : { title: 'Save failed', message: vm.saveError ?? 'Please try again.', color: 'red' },
        );
    };

    return (
        <Box>
            <PageHeaderComponent
                title="Data & Retention"
                subtitle="Control how long data is kept, what is never stored, and who processes it."
            />

            {vm.loading && !vm.policy ? (
                <Center py="xl">
                    <Loader />
                </Center>
            ) : vm.error && !vm.policy ? (
                <Alert variant="light" color="red" icon={<AlertTriangle size={16} />}>
                    {vm.error}
                </Alert>
            ) : (
                <>
                    <DataResidencyCard />

                    <Tabs value={tab} onChange={setTab} keepMounted={false}>
                        <Tabs.List mb="xl">
                            <Tabs.Tab value="retention" leftSection={<Clock size={14} />}>
                                Retention
                            </Tabs.Tab>
                            <Tabs.Tab value="exclusions" leftSection={<EyeOff size={14} />}>
                                Exclusions
                            </Tabs.Tab>
                            <Tabs.Tab value="subprocessors" leftSection={<ShieldCheck size={14} />}>
                                Subprocessors
                            </Tabs.Tab>
                            <Tabs.Tab value="danger" color="red" leftSection={<AlertTriangle size={14} />}>
                                Danger zone
                            </Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="retention">
                            <RetentionTab vm={vm} />
                        </Tabs.Panel>
                        <Tabs.Panel value="exclusions">
                            <ExclusionsTab vm={vm} />
                        </Tabs.Panel>
                        <Tabs.Panel value="subprocessors">
                            <SubprocessorsTab />
                        </Tabs.Panel>
                        <Tabs.Panel value="danger">
                            <DangerTab onDelete={() => setDeleteOpen(true)} />
                        </Tabs.Panel>
                    </Tabs>

                    {(tab === 'retention' || tab === 'exclusions') && (
                        <Group mt="xl">
                            <Button onClick={() => void save()} loading={vm.saving} disabled={!vm.dirty}>
                                Save changes
                            </Button>
                            {vm.dirty && (
                                <Text size="xs" c="dimmed">
                                    You have unsaved changes.
                                </Text>
                            )}
                        </Group>
                    )}
                </>
            )}

            <Modal opened={deleteOpen} onClose={closeDelete} title="Delete all data" centered>
                <Stack gap="md">
                    <Text size="sm">
                        This permanently deletes <strong>all collected data</strong> in this workspace — telemetry,
                        analytics, license history, and insights. Your account, settings, and connections stay. To
                        confirm, type <strong>DELETE</strong> below.
                    </Text>
                    <TextInput
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.currentTarget.value)}
                        placeholder="DELETE"
                        autoFocus
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={closeDelete}>
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            disabled={confirmText !== 'DELETE'}
                            loading={vm.purging}
                            onClick={() => void confirmDelete()}
                        >
                            Permanently delete
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Box>
    );
}
